'use client';

import { useEffect, useState } from 'react';
import type { SiteConfig, SiteShareService } from '@/server.api/types';

type ArticleShareMenuProps = {
    title: string;
    url: string;
    labels: SiteConfig['labels']['share'];
    services: SiteShareService[];
    copyResetMs: number;
};

type CopyStatus = 'idle' | 'copied' | 'unavailable';

// Tabler Icons, MIT License: https://github.com/tabler/tabler-icons
const shareIconPaths: Record<string, string[]> = {
    facebook: ['M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3'],
    x: ['M4 4l11.733 16h4.267l-11.733 -16l-4.267 0', 'M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772'],
    whatsapp: [
        'M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9',
        'M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1',
    ],
    telegram: ['M15 10l-4 4l6 6l4 -16l-18 7l4 2l2 6l3 -4'],
    copy: [
        'M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667l0 -8.666',
        'M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1',
    ],
};

function ShareIcon({ serviceId }: { serviceId: string }) {
    const paths = shareIconPaths[serviceId] ?? shareIconPaths.copy;

    return (
        <svg
            className='article-share-menu__icon'
            aria-hidden='true'
            viewBox='0 0 24 24'
            focusable='false'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
        >
            {paths.map((path) => (
                <path key={path} d={path} />
            ))}
        </svg>
    );
}

export default function ArticleShareMenu({
    title,
    url,
    labels,
    services,
    copyResetMs,
}: ArticleShareMenuProps) {
    const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
    const encodedTitle = encodeURIComponent(title);
    const encodedUrl = encodeURIComponent(url);

    useEffect(() => {
        if (copyStatus === 'idle') return;

        const timeoutId = window.setTimeout(() => setCopyStatus('idle'), copyResetMs);
        return () => window.clearTimeout(timeoutId);
    }, [copyResetMs, copyStatus]);

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(url);
            setCopyStatus('copied');
        } catch {
            setCopyStatus('unavailable');
        }
    }

    function shareHref(service: SiteShareService) {
        return service.urlTemplate
            .replace(/\{url\}/g, encodedUrl)
            .replace(/\{title\}/g, encodedTitle);
    }

    const copyLabel =
        copyStatus === 'copied'
            ? labels.copied
            : copyStatus === 'unavailable'
              ? labels.unavailable
              : labels.copy;

    return (
        <div className='article-share-menu'>
            {services.map((service) =>
                service.id === 'copy' ? (
                    <button
                        key={service.id}
                        type='button'
                        className={`article-share-menu__item${copyStatus === 'copied' ? ' article-share-menu__item--copied' : ''}`}
                        aria-label={copyLabel}
                        title={copyLabel}
                        onClick={copyLink}
                    >
                        <ShareIcon serviceId={service.id} />
                    </button>
                ) : (
                    <a
                        key={service.id}
                        href={shareHref(service)}
                        className='article-share-menu__item'
                        aria-label={service.label}
                        title={service.label}
                        target='_blank'
                        rel='noopener noreferrer nofollow'
                    >
                        <ShareIcon serviceId={service.id} />
                    </a>
                ),
            )}
            <span className='article-share-menu__status' aria-live='polite'>
                {copyStatus === 'idle' ? '' : copyLabel}
            </span>
        </div>
    );
}
